import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

/**
 * PwaInstallBanner — single-line dismissible bar pinned above the
 * bottom tab bar. See Amanah 2.0 rebuild directive Phase 0.2.
 *
 * Show rules:
 *   • Home route (/) only.
 *   • Mobile only (<1024px).
 *   • Only after the user has scrolled past the first viewport height.
 *   • Never on calculator routes (/zakat/calculator, /inheritance, /will,
 *     /succession, /business-calculator).
 *   • Dismissal persists 90 days per device in localStorage.
 *   • Auto-hides when the app is already installed (display-mode: standalone).
 *
 * Interaction:
 *   • Chrome/Edge: taps Install → native beforeinstallprompt flow.
 *   • iOS Safari: no native prompt exists, so we show a compact hint
 *     with the standard Share → Add to Home Screen instruction.
 */
const DISMISS_KEY = 'amanah_pwa_dismiss_at';
const DISMISS_DAYS = 90;
const CALC_ROUTES = ['/zakat/calculator', '/inheritance', '/will', '/succession', '/business-calculator'];

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIOS = () => /iPad|iPhone|iPod/.test(window.navigator.userAgent || '');

const wasRecentlyDismissed = () => {
  try {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!t) return false;
    return Date.now() - t < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch { return false; }
};

export const PwaInstallBanner = () => {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Track beforeinstallprompt so we can trigger the native flow later.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (isStandalone() || wasRecentlyDismissed()) { setDismissed(true); return undefined; }

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => {
      setDismissed(true);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Only surface once the user has moved past the first viewport.
  useEffect(() => {
    if (dismissed) return undefined;
    const check = () => {
      if (window.scrollY >= window.innerHeight * 0.9) setScrolled(true);
    };
    check();
    window.addEventListener('scroll', check, { passive: true });
    return () => window.removeEventListener('scroll', check);
  }, [dismissed]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    setDismissed(true);
    if (choice?.outcome !== 'accepted') {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    }
  }, [deferred]);

  // Gate rules
  if (dismissed) return null;
  if (pathname !== '/') return null;
  if (CALC_ROUTES.some((r) => pathname.startsWith(r))) return null;
  if (!scrolled) return null;

  const ios = isIOS();
  // On non-iOS we need the deferred prompt; if it never fires, the browser
  // doesn't consider us installable — stay silent rather than misleading.
  if (!ios && !deferred) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="pwa-install-banner"
      className="fixed left-0 right-0 z-[45] lg:hidden bottom-[calc(56px+env(safe-area-inset-bottom))]"
    >
      <div className="mx-3 mb-2 flex items-center gap-3 h-11 px-3 rounded-full bg-[#EDF5F1] text-[#0D1520] backdrop-blur-md border border-gold-500/40 shadow-lg">
        <span className="text-[13px] font-medium truncate flex-1">
          {ios ? 'Install Amanah — tap Share → Add to Home Screen' : 'Add Amanah to your home screen'}
        </span>
        {!ios && (
          <button
            type="button"
            onClick={install}
            data-testid="pwa-install-btn"
            className="shrink-0 h-8 px-3 rounded-full bg-gold-500 text-[#0D1520] text-[12px] font-semibold hover:bg-gold-400 transition-colors"
          >
            Install
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          data-testid="pwa-install-dismiss"
          aria-label="Dismiss install prompt"
          className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[#4A5568] hover:text-[#0D1520] hover:bg-[#EFE7D8] transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

export default PwaInstallBanner;
